import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Lock, Plus, Pencil, Trash2, X, Loader2, LogOut, Star } from "lucide-react";
import { STORE_CATEGORIES, catName } from "../../store/components";
import { LLAdmin } from "./LLAdmin";
import { MediaAdmin, uploadFile } from "./MediaAdmin";
import { DashboardCounts, CustomersAdmin, ModulesAdmin, SuccessStoriesAdmin, VisitorInsights } from "./AdminExtras";
import { inr } from "../../store/CartContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-dwc-charcoal outline-none focus:border-dwc-green";
const EMPTY = {
  name: "", slug: "", category: "car-utility", price: "", mrp: "", short_benefit: "", description: "",
  features: "", included: "", how_to_use: "", safety_note: "", images: "", stock: 25, recommended: false, active: true,
};
const ORDER_STATUSES = ["placed", "confirmed", "shipped", "delivered", "cancelled"];
const PAY_STATUSES = ["cod_pending", "pending", "paid", "failed", "refunded"];

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const Login = ({ onLogin }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(`${API}/api/store/admin/login`, form);
      onLogin(r.data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    }
    setLoading(false);
  };
  return (
    <section className="flex min-h-screen items-center justify-center bg-dwc-soft px-5 pt-20" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-gray-100 bg-white p-8 shadow-xl">
        <Lock className="h-8 w-8 text-dwc-green" aria-hidden="true" />
        <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-dwc-charcoal">Store Admin</h1>
        <p className="mt-1 text-xs text-dwc-charcoal/55">Authorized DWC team members only.</p>
        <div className="mt-6 space-y-3">
          <input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Admin Email" aria-label="Admin email" data-testid="admin-email-input" className={inputCls} />
          <input required type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Password" aria-label="Password" data-testid="admin-password-input" className={inputCls} />
        </div>
        {error && <p className="mt-3 text-sm font-bold text-dwc-crimson" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-btn" className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-dwc-green py-3.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-dwc-green-dark disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : "Login"}
        </button>
      </form>
    </section>
  );
};

const toPayload = (f) => ({
  ...f,
  price: parseFloat(f.price) || 0,
  mrp: f.mrp ? parseFloat(f.mrp) : null,
  stock: parseInt(f.stock, 10) || 0,
  features: String(f.features).split("\n").map((s) => s.trim()).filter(Boolean),
  included: String(f.included).split("\n").map((s) => s.trim()).filter(Boolean),
  images: String(f.images).split("\n").map((s) => s.trim()).filter(Boolean),
});

const fromProduct = (p) => ({
  ...EMPTY, ...p,
  features: (p.features || []).join("\n"),
  included: (p.included || []).join("\n"),
  images: (p.images || []).join("\n"),
});

const ProductModal = ({ product, token, onClose, onSaved }) => {
  const [form, setForm] = useState(product ? fromProduct(product) : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (product?.id) await axios.put(`${API}/api/store/admin/products/${product.id}`, toPayload(form), auth(token));
      else await axios.post(`${API}/api/store/admin/products`, toPayload(form), auth(token));
      toast.success("Product saved");
      onSaved();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Save failed");
    }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-[95] flex items-start justify-center overflow-y-auto bg-dwc-charcoal/60 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <form onSubmit={save} className="my-8 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" data-testid="product-modal">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-dwc-charcoal">{product ? "Edit Product" : "Add Product"}</h2>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="product-modal-close" className="rounded-lg p-2 text-dwc-charcoal/50 hover:bg-dwc-soft"><X className="h-5 w-5" aria-hidden="true" /></button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <input required value={form.name} onChange={set("name")} placeholder="Product Name *" data-testid="pm-name" className={`${inputCls} sm:col-span-2`} />
          <select value={form.category} onChange={set("category")} data-testid="pm-category" className={inputCls}>
            {STORE_CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <input value={form.slug} onChange={set("slug")} placeholder="Slug (auto if empty)" data-testid="pm-slug" className={inputCls} />
          <input required type="number" min="1" step="0.01" value={form.price} onChange={set("price")} placeholder="Price ₹ *" data-testid="pm-price" className={inputCls} />
          <input type="number" min="0" step="0.01" value={form.mrp || ""} onChange={set("mrp")} placeholder="MRP ₹ (optional)" data-testid="pm-mrp" className={inputCls} />
          <input type="number" min="0" value={form.stock} onChange={set("stock")} placeholder="Stock" data-testid="pm-stock" className={inputCls} />
          <div className="flex items-center gap-5 px-1">
            <label className="flex items-center gap-2 text-xs font-bold text-dwc-charcoal/70"><input type="checkbox" checked={form.recommended} onChange={set("recommended")} data-testid="pm-recommended" className="accent-dwc-green" /> DWC Recommended</label>
            <label className="flex items-center gap-2 text-xs font-bold text-dwc-charcoal/70"><input type="checkbox" checked={form.active} onChange={set("active")} data-testid="pm-active" className="accent-dwc-green" /> Active</label>
          </div>
          <input value={form.short_benefit} onChange={set("short_benefit")} placeholder="Short benefit (one line)" data-testid="pm-benefit" className={`${inputCls} sm:col-span-2`} />
          <textarea rows={3} value={form.description} onChange={set("description")} placeholder="Description" data-testid="pm-description" className={`${inputCls} sm:col-span-2`} />
          <textarea rows={3} value={form.features} onChange={set("features")} placeholder="Features (one per line)" data-testid="pm-features" className={inputCls} />
          <textarea rows={3} value={form.included} onChange={set("included")} placeholder="What's included (one per line)" data-testid="pm-included" className={inputCls} />
          <textarea rows={2} value={form.how_to_use} onChange={set("how_to_use")} placeholder="How to use" className={inputCls} />
          <textarea rows={2} value={form.safety_note} onChange={set("safety_note")} placeholder="Safety note (optional)" className={inputCls} />
          <textarea rows={2} value={form.images} onChange={set("images")} placeholder="Image URLs (one per line)" data-testid="pm-images" className={`${inputCls} sm:col-span-2`} />
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-dwc-green/40 px-3 py-2.5 text-xs font-bold text-dwc-green sm:col-span-2">
            Upload product image
            <input
              type="file"
              accept="image/*"
              data-testid="pm-image-upload"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const up = await uploadFile(file, token);
                  setForm((f) => ({ ...f, images: (f.images ? f.images + "\n" : "") + up.url }));
                  toast.success("Image uploaded");
                } catch { toast.error("Upload failed"); }
                e.target.value = "";
              }}
            />
          </label>
        </div>
        <button type="submit" disabled={saving} data-testid="pm-save-btn" className="mt-5 w-full rounded-xl bg-dwc-green py-3.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-dwc-green-dark disabled:opacity-60">
          {saving ? "Saving..." : "Save Product"}
        </button>
      </form>
    </div>
  );
};

const StoreAdmin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem("dwc-admin-token") || "");
  const [tab, setTab] = useState("orders");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [modal, setModal] = useState(null); // null | "new" | product

  const logout = () => { sessionStorage.removeItem("dwc-admin-token"); setToken(""); };

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [p, o, s] = await Promise.all([
        axios.get(`${API}/api/store/admin/products`, auth(token)),
        axios.get(`${API}/api/store/admin/orders`, auth(token)),
        axios.get(`${API}/api/store/settings`),
      ]);
      setProducts(p.data);
      setOrders(o.data);
      setSettings(s.data);
    } catch (err) {
      if (err.response?.status === 401) logout();
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!token) return <Login onLogin={(t) => { sessionStorage.setItem("dwc-admin-token", t); setToken(t); }} />;

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    await axios.delete(`${API}/api/store/admin/products/${p.id}`, auth(token));
    toast.success("Product deleted");
    load();
  };

  const patchOrder = async (o, patch) => {
    await axios.patch(`${API}/api/store/admin/orders/${o.id}`, patch, auth(token));
    toast.success("Order updated");
    load();
  };

  const setFeatured = async (p) => {
    const next = settings?.featured_product_id === p.id ? "" : p.id;
    await axios.put(`${API}/api/store/admin/featured`, { product_id: next }, auth(token));
    setSettings((s) => ({ ...s, featured_product_id: next }));
    toast.success(next ? `"${p.name}" is now the featured cart item` : "Featured item cleared (newest in-stock product will be used)");
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    await axios.put(`${API}/api/store/admin/settings`, {
      ...settings,
      shipping_fee: parseFloat(settings.shipping_fee) || 0,
      free_shipping_above: parseFloat(settings.free_shipping_above) || 0,
    }, auth(token));
    toast.success("Settings saved");
  };

  return (
    <section className="allow-select min-h-screen bg-dwc-soft pb-24 pt-28 md:pt-32" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-extrabold tracking-tight text-dwc-charcoal">DWC Store Admin</h1>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-dwc-charcoal/70 hover:text-dwc-crimson">
            <LogOut className="h-4 w-4" aria-hidden="true" /> Logout
          </button>
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          {["orders", "products", "stories", "customers", "insights", "media", "modules", "settings", "licence"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`rounded-full px-4 py-2.5 text-xs font-extrabold uppercase tracking-wide ${tab === t ? "bg-dwc-green text-white" : "border border-dwc-green/25 bg-white text-dwc-green"}`}>
              {t === "licence" ? "Licence Questions" : t === "modules" ? "Exam Modules" : t === "insights" ? "Visitor Insights" : t} {t === "orders" ? `(${orders.length})` : t === "products" ? `(${products.length})` : ""}
            </button>
          ))}
        </div>
        <DashboardCounts token={token} refreshKey={tab} />

        {tab === "orders" && (
          <div className="mt-6 space-y-3" data-testid="admin-orders-list">
            {orders.length === 0 && <p className="rounded-2xl bg-white p-8 text-center text-sm font-semibold text-dwc-charcoal/50">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.id} className="rounded-2xl border border-gray-100 bg-white p-5" data-testid={`admin-order-${o.order_number}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-black text-dwc-green">{o.order_number} <span className="ml-2 text-xs font-bold text-dwc-charcoal/50">{new Date(o.created_at).toLocaleString("en-IN")}</span></p>
                    <p className="mt-1 text-sm font-bold text-dwc-charcoal">{o.customer.name} · {o.customer.mobile} · {o.customer.city}, {o.customer.pincode}</p>
                    <p className="mt-0.5 text-xs text-dwc-charcoal/60">{o.customer.address}</p>
                    <p className="mt-1 text-xs font-semibold text-dwc-charcoal/70">{o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-lg font-black text-dwc-charcoal">{inr(o.total)}</span>
                    <select value={o.payment_status} onChange={(e) => patchOrder(o, { payment_status: e.target.value })} aria-label="Payment status" className="rounded-lg border border-gray-200 px-2.5 py-2 text-xs font-bold">
                      {PAY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={o.order_status} onChange={(e) => patchOrder(o, { order_status: e.target.value })} aria-label="Order status" data-testid={`order-status-select-${o.order_number}`} className="rounded-lg border border-dwc-green/30 bg-dwc-soft px-2.5 py-2 text-xs font-black text-dwc-green">
                      {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "products" && (
          <>
            <button onClick={() => setModal("new")} data-testid="admin-add-product-btn" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-dwc-green px-5 py-3 text-xs font-bold uppercase tracking-wide text-white hover:bg-dwc-green-dark">
              <Plus className="h-4 w-4" aria-hidden="true" /> Add Product
            </button>
            <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-100 bg-white" data-testid="admin-products-table">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-[11px] font-extrabold uppercase tracking-wide text-dwc-charcoal/50">
                    <th className="px-4 py-3">Product</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Stock</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-gray-50" data-testid={`admin-product-row-${p.slug}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img src={(p.images || [])[0]} alt="" className="h-10 w-10 rounded-lg bg-dwc-soft object-cover" loading="lazy" />
                          <span className="font-bold text-dwc-charcoal">{p.name} {p.recommended && <span className="ml-1 rounded bg-dwc-green px-1.5 py-0.5 text-[9px] font-black text-white">REC</span>}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs font-bold text-dwc-charcoal/60">{catName(p.category)}</td>
                      <td className="px-4 py-3 font-black">{inr(p.price)}</td>
                      <td className={`px-4 py-3 font-bold ${p.stock < 5 ? "text-dwc-crimson" : ""}`}>{p.stock}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase ${p.active ? "bg-dwc-soft text-dwc-green" : "bg-gray-100 text-gray-500"}`}>{p.active ? "Active" : "Hidden"}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setFeatured(p)} aria-label={`Feature ${p.name} in cart`} title="Featured cart item" data-testid={`feature-product-${p.slug}`} className={`rounded-lg p-2 ${settings?.featured_product_id === p.id ? "text-amber-500" : "text-gray-300 hover:text-amber-400"}`}><Star className="h-4 w-4" fill={settings?.featured_product_id === p.id ? "currentColor" : "none"} aria-hidden="true" /></button>
                        <button onClick={() => setModal(p)} aria-label={`Edit ${p.name}`} data-testid={`edit-product-${p.slug}`} className="rounded-lg p-2 text-dwc-green hover:bg-dwc-soft"><Pencil className="h-4 w-4" aria-hidden="true" /></button>
                        <button onClick={() => del(p)} aria-label={`Delete ${p.name}`} data-testid={`delete-product-${p.slug}`} className="rounded-lg p-2 text-dwc-crimson hover:bg-dwc-crimson/5"><Trash2 className="h-4 w-4" aria-hidden="true" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "licence" && <LLAdmin token={token} />}
        {tab === "media" && <MediaAdmin token={token} />}
        {tab === "customers" && <CustomersAdmin token={token} />}
        {tab === "insights" && <VisitorInsights token={token} />}
        {tab === "stories" && <SuccessStoriesAdmin token={token} />}
        {tab === "modules" && <ModulesAdmin token={token} />}

        {tab === "settings" && settings && (
          <form onSubmit={saveSettings} className="mt-6 max-w-lg space-y-4 rounded-2xl border border-gray-100 bg-white p-6" data-testid="admin-settings-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-dwc-charcoal/60" htmlFor="set-fee">Shipping Fee (₹)</label>
              <input id="set-fee" type="number" min="0" value={settings.shipping_fee} onChange={(e) => setSettings((s) => ({ ...s, shipping_fee: e.target.value }))} data-testid="settings-shipping-fee" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-dwc-charcoal/60" htmlFor="set-free">Free Shipping Above (₹)</label>
              <input id="set-free" type="number" min="0" value={settings.free_shipping_above} onChange={(e) => setSettings((s) => ({ ...s, free_shipping_above: e.target.value }))} data-testid="settings-free-above" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-dwc-charcoal/60" htmlFor="set-delivery">Delivery Estimate Text</label>
              <input id="set-delivery" value={settings.delivery_estimate} onChange={(e) => setSettings((s) => ({ ...s, delivery_estimate: e.target.value }))} data-testid="settings-delivery-estimate" className={inputCls} />
            </div>
            <button type="submit" data-testid="settings-save-btn" className="w-full rounded-xl bg-dwc-green py-3.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-dwc-green-dark">Save Settings</button>
          </form>
        )}
      </div>
      {modal && <ProductModal product={modal === "new" ? null : modal} token={token} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </section>
  );
};

export default StoreAdmin;
